import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const movementTypeKeys = {
  all: ['movementTypes'],
  lists: () => [...movementTypeKeys.all, 'list'],
  details: () => [...movementTypeKeys.all, 'detail'],
  detail: (id) => [...movementTypeKeys.details(), id],
  byNature: (nature) => [...movementTypeKeys.all, 'nature', nature],
  byDescription: (description) => [...movementTypeKeys.all, 'description', description],
};

/**
 * Hook para obtener todos los tipos de movimiento
 */
export const useMovementTypes = () => {
  return useQuery({
    queryKey: movementTypeKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('movement_types');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un tipo de movimiento específico
 * @param {string} id - UUID del tipo de movimiento
 * @param {Object} options - Opciones adicionales para la query
 */
export const useMovementType = (id, options = {}) => {
  return useQuery({
    queryKey: movementTypeKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`movement_types/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener tipos de movimiento por naturaleza
 * @param {string} nature - Naturaleza del movimiento ('debit' o 'credit')
 * @param {Object} options - Opciones adicionales para la query
 */
export const useMovementTypesByNature = (nature, options = {}) => {
  return useQuery({
    queryKey: movementTypeKeys.byNature(nature),
    queryFn: async () => {
      const response = await apiClient.get(`movement_types/by-nature/${nature}`);
      return response.data;
    },
    enabled: !!nature && ['debit', 'credit'].includes(nature),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener un tipo de movimiento por descripción
 * @param {string} description - Descripción del tipo de movimiento
 * @param {Object} options - Opciones adicionales para la query
 */
export const useMovementTypeByDescription = (description, options = {}) => {
  return useQuery({
    queryKey: movementTypeKeys.byDescription(description),
    queryFn: async () => {
      const response = await apiClient.get(`movement_types/by-description/${description}`);
      return response.data;
    },
    enabled: !!description,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo tipo de movimiento
 */
export const useCreateMovementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (movementTypeData) => {
      const response = await apiClient.post('movement_types', movementTypeData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.all });
    },
    onError: (error) => {
      console.error('Error creating movement type:', error);
    },
  });
};

/**
 * Hook para actualizar un tipo de movimiento existente
 */
export const useUpdateMovementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`movement_types/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(movementTypeKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.all });
    },
    onError: (error) => {
      console.error('Error updating movement type:', error);
    },
  });
};

/**
 * Hook para eliminar un tipo de movimiento
 */
export const useDeleteMovementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`movement_types/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: movementTypeKeys.all });
      queryClient.removeQueries({ queryKey: movementTypeKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting movement type:', error);
    },
  });
};
